import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, Card, Form } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';
import { getCitiesByCountry } from '../../services/SelectorsService';
import { useLanguage } from '../../i18n/LanguageContext';

function Accordsidebar({ selectors = {}, filters = {}, onFilterChange, onReset }) {
	const [localFilters, setLocalFilters] = useState({
		modalidadTrabajoId: filters.modalidadTrabajoId || '',
		tipoTrabajoId: filters.tipoTrabajoId || '',
		tipoContratoTrabajoId: filters.tipoContratoTrabajoId || '',
		tiempoExperienciaRequeridoId: filters.tiempoExperienciaRequeridoId || '',
		categoriaId: filters.categoriaId || '',
		paisId: filters.paisId || '',
		ciudadId: filters.ciudadId || '',
		salarioMinimo: filters.salarioMinimo || '',
		salarioMaximo: filters.salarioMaximo || '',
		inglesRequerido: filters.inglesRequerido !== undefined ? filters.inglesRequerido : null,
		tags: filters.tags || ''
	});
	const [availableCities, setAvailableCities] = useState([]);

	const { t } = useLanguage();

	// Sincronizar con props cuando cambian
	useEffect(() => {
		setLocalFilters({
			modalidadTrabajoId: filters.modalidadTrabajoId || '',
			tipoTrabajoId: filters.tipoTrabajoId || '',
			tipoContratoTrabajoId: filters.tipoContratoTrabajoId || '',
			tiempoExperienciaRequeridoId: filters.tiempoExperienciaRequeridoId || '',
			categoriaId: filters.categoriaId || '',
			paisId: filters.paisId || '',
			ciudadId: filters.ciudadId || '',
			salarioMinimo: filters.salarioMinimo || '',
			salarioMaximo: filters.salarioMaximo || '',
			inglesRequerido: filters.inglesRequerido !== undefined ? filters.inglesRequerido : null,
			tags: filters.tags || ''
		});
	}, [filters]);

	// Cargar ciudades cuando cambia el país
	useEffect(() => {
		const loadCities = async () => {
			if (localFilters.paisId) {
				try {
					const response = await getCitiesByCountry(localFilters.paisId);
					setAvailableCities(response.data || []);
				} catch (err) {
					console.error('Error loading cities:', err);
					setAvailableCities([]);
				}
			} else {
				setAvailableCities([]);
			}
		};

		loadCities();
	}, [localFilters.paisId]);

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		const newValue = type === 'checkbox' ? checked : value;
		
		const updatedFilters = {
			...localFilters,
			[name]: newValue
		};

		// Si cambia el país, limpiar ciudad
		if (name === 'paisId') {
			updatedFilters.ciudadId = '';
			setAvailableCities([]);
		}

		setLocalFilters(updatedFilters);
		
		// Notificar cambio al componente padre
		if (onFilterChange) {
			onFilterChange({ [name]: newValue === '' ? undefined : newValue });
		}
	};

	const handleNumericChange = (name, value) => {
		const numValue = value ? parseFloat(value.replace(/,/g, '')) : '';
		const updatedFilters = {
			...localFilters,
			[name]: numValue
		};
		setLocalFilters(updatedFilters);
		
		if (onFilterChange) {
			onFilterChange({ [name]: numValue === '' ? undefined : numValue });
		}
	};

	const handleTagsChange = (e) => {
		const value = e.target.value;
		setLocalFilters(prev => ({ ...prev, tags: value }));
		
		if (onFilterChange) {
			onFilterChange({ tags: value === '' ? undefined : value });
		}
	};

	const handleReset = () => {
		const resetFilters = {
			modalidadTrabajoId: '',
			tipoTrabajoId: '',
			tipoContratoTrabajoId: '',
			tiempoExperienciaRequeridoId: '',
			categoriaId: '',
			paisId: '',
			ciudadId: '',
			salarioMinimo: '',
			salarioMaximo: '',
			inglesRequerido: null,
			tags: ''
		};
		setLocalFilters(resetFilters);
		setAvailableCities([]);
		
		if (onReset) {
			onReset();
		}
	};

	return (
		<div className="col-xl-3 col-lg-4 col-md-5 m-b30">
			<aside id="accordion1" className="sticky-top sidebar-filter bg-white">
				<Accordion defaultActiveKey="0">
					<h6 className="title">
						<i className="fa fa-sliders m-r5"></i> {t('jobsection.filterBy')}
						<Link to="#" onClick={(e) => { e.preventDefault(); handleReset(); }} className="font-12 float-right">
							{t('jobsection.resetAll')}
						</Link>
					</h6>

					{/* Acordeón 1: Tipo de Trabajo */}
					<Accordion.Toggle as={Card} eventKey="0">
						<div className="acod-head">
							<h6 className="acod-title">
								<a href="#" onClick={(e) => e.preventDefault()} style={{ cursor: 'pointer' }}>
									{t('jobsection.workType')}
								</a>
							</h6>
						</div>
					</Accordion.Toggle>
					<Accordion.Collapse eventKey="0">
						<div id="tipo-trabajo" className="acod-body">
							<div className="acod-content">
								<div className="form-group">
									<label>{t('jobsection.modalidadLabel')}</label>
									<Form.Control
										as="select"
										className="form-control"
										name="modalidadTrabajoId"
										value={localFilters.modalidadTrabajoId}
										onChange={handleInputChange}
									>
										<option value="">{t('jobsection.allModalities')}</option>
										{selectors.modalidadesTrabajo && selectors.modalidadesTrabajo.map((modalidad) => (
											<option key={modalidad.id} value={modalidad.id}>
												{modalidad.nombre}
											</option>
										))}
									</Form.Control>
								</div>
								<div className="form-group">
									<label>{t('jobsection.tipoTrabajoLabel')}</label>
									<Form.Control
										as="select"
										className="form-control"
										name="tipoTrabajoId"
										value={localFilters.tipoTrabajoId}
										onChange={handleInputChange}
									>
										<option value="">{t('jobsection.allTypes')}</option>
										{selectors.tiposTrabajo && selectors.tiposTrabajo.map((tipo) => (
											<option key={tipo.id} value={tipo.id}>
												{tipo.nombre}
											</option>
										))}
									</Form.Control>
								</div>
								<div className="form-group">
									<label>{t('jobsection.tipoContratoLabel')}</label>
									<Form.Control
										as="select"
										className="form-control"
										name="tipoContratoTrabajoId"
										value={localFilters.tipoContratoTrabajoId}
										onChange={handleInputChange}
									>
										<option value="">{t('jobsection.allContracts')}</option>
										{selectors.tiposContrato && selectors.tiposContrato.map((tipo) => (
											<option key={tipo.id} value={tipo.id}>
												{tipo.nombre}
											</option>
										))}
									</Form.Control>
								</div>
							</div>
						</div>
					</Accordion.Collapse>

					{/* Acordeón 2: Experiencia, Categoría y Tags */}
					<Accordion.Toggle as={Card} eventKey="1">
						<div className="acod-head">
							<h6 className="acod-title">
								<a href="#" onClick={(e) => e.preventDefault()} style={{ cursor: 'pointer' }}>
									{t('jobsection.experienceCategory')}
								</a>
							</h6>
						</div>
					</Accordion.Toggle>
					<Accordion.Collapse eventKey="1">
						<div id="experiencia-categoria-tags" className="acod-body">
							<div className="acod-content">
								<div className="form-group">
									<label>{t('jobsection.experienceLabel')}</label>
									<Form.Control
										as="select"
										className="form-control"
										name="tiempoExperienciaRequeridoId"
										value={localFilters.tiempoExperienciaRequeridoId}
										onChange={handleInputChange}
									>
										<option value="">{t('jobsection.anyExperience')}</option>
										{selectors.tiemposExperiencia && selectors.tiemposExperiencia.map((tiempo) => (
											<option key={tiempo.id} value={tiempo.id}>
												{tiempo.nombre}
											</option>
										))}
									</Form.Control>
								</div>
								<div className="form-group">
									<label>{t('jobsection.categoryLabel')}</label>
									<Form.Control
										as="select"
										className="form-control"
										name="categoriaId"
										value={localFilters.categoriaId}
										onChange={handleInputChange}
									>
										<option value="">{t('jobsection.allCategories')}</option>
										{selectors.categorias && selectors.categorias.map((categoria) => (
											<option key={categoria.id} value={categoria.id}>
												{categoria.nombre}
											</option>
										))}
									</Form.Control>
								</div>
								<div className="form-group">
									<label>{t('jobsection.tagsLabel')}</label>
									<input
										type="text"
										className="form-control"
										placeholder={t('jobsection.tagsPlaceholder')}
										name="tags"
										value={localFilters.tags}
										onChange={handleTagsChange}
									/>
									<small className="form-text text-muted">
										{t('jobsection.tagsHelp')}
									</small>
								</div>
							</div>
						</div>
					</Accordion.Collapse>

					{/* Acordeón 3: Compensación */}
					<Accordion.Toggle as={Card} eventKey="2">
						<div className="acod-head">
							<h6 className="acod-title">
								<a href="#" onClick={(e) => e.preventDefault()} style={{ cursor: 'pointer' }}>
									{t('jobsection.compensation')}
								</a>
							</h6>
						</div>
					</Accordion.Toggle>
					<Accordion.Collapse eventKey="2">
						<div id="compensacion" className="acod-body">
							<div className="acod-content">
								<div className="form-group">
									<label>{t('jobsection.minSalaryLabel')}</label>
									<NumericFormat
										className="form-control"
										prefix="RD$ "
										thousandSeparator
										placeholder="0"
										value={localFilters.salarioMinimo}
										onValueChange={(values) => handleNumericChange('salarioMinimo', values.value)}
									/>
								</div>
								<div className="form-group">
									<label>{t('jobsection.maxSalaryLabel')}</label>
									<NumericFormat
										className="form-control"
										prefix="RD$ "
										thousandSeparator
										placeholder="0"
										value={localFilters.salarioMaximo}
										onValueChange={(values) => handleNumericChange('salarioMaximo', values.value)}
									/>
								</div>
							</div>
						</div>
					</Accordion.Collapse>

					{/* Acordeón 4: Ubicación */}
					<Accordion.Toggle as={Card} eventKey="3">
						<div className="acod-head">
							<h6 className="acod-title">
								<a href="#" onClick={(e) => e.preventDefault()} style={{ cursor: 'pointer' }}>
									{t('jobsection.location')}
								</a>
							</h6>
						</div>
					</Accordion.Toggle>
					<Accordion.Collapse eventKey="3">
						<div id="ubicacion" className="acod-body">
							<div className="acod-content">
								<div className="form-group">
									<label>{t('jobsection.countryLabel')}</label>
									<Form.Control
										as="select"
										className="form-control"
										name="paisId"
										value={localFilters.paisId}
										onChange={handleInputChange}
									>
										<option value="">Todos los países</option>
										{selectors.paises && selectors.paises.map((pais) => (
											<option key={pais.id} value={pais.id}>
												{pais.nombre}
											</option>
										))}
									</Form.Control>
								</div>
								<div className="form-group">
									<label>{t('jobsection.cityLabel')}</label>
									<Form.Control
										as="select"
										className="form-control"
										name="ciudadId"
										value={localFilters.ciudadId}
										onChange={handleInputChange}
										disabled={!localFilters.paisId}
									>
										<option value="">
											{localFilters.paisId ? t('jobsection.allCities') : t('jobsection.firstSelectCountry')}
										</option>
										{availableCities.map((ciudad) => (
											<option key={ciudad.id} value={ciudad.id}>
												{ciudad.nombre}
											</option>
										))}
									</Form.Control>
								</div>
							</div>
						</div>
					</Accordion.Collapse>

					{/* Acordeón 5: Requisitos Adicionales */}
					<Accordion.Toggle as={Card} eventKey="4">
						<div className="acod-head">
							<h6 className="acod-title">
								<a href="#" onClick={(e) => e.preventDefault()} style={{ cursor: 'pointer' }}>
									{t('jobsection.additionalRequirements')}
								</a>
							</h6>
						</div>
					</Accordion.Toggle>
					<Accordion.Collapse eventKey="4">
						<div id="requisitos-adicionales" className="acod-body">
							<div className="acod-content">
								<div className="custom-control custom-checkbox">
									<input
										type="checkbox"
										className="custom-control-input"
										id="inglesRequerido"
										name="inglesRequerido"
										checked={localFilters.inglesRequerido === true}
										onChange={(e) => {
											const checked = e.target.checked;
											setLocalFilters(prev => ({ ...prev, inglesRequerido: checked ? true : null }));
											if (onFilterChange) {
												onFilterChange({ inglesRequerido: checked ? true : undefined });
											}
										}}
									/>
									<label className="custom-control-label" htmlFor="inglesRequerido">
										{t('jobsection.englishRequired')}
									</label>
								</div>
							</div>
						</div>
					</Accordion.Collapse>

				</Accordion>
			</aside>
		</div>
	)
}
export default Accordsidebar;
